/**
 * GuestWorkOrderRoute — /guest/:token/wo/:workOrderKey
 * Loads one work order the link's user is delegated (directly, or through the
 * project it belongs to) and renders GuestTaskPage. Open work orders are
 * interactive; one completed within 30 days comes back in the same shape and
 * renders read-only. Opened from a project page (router state `projectKey`)
 * the back link returns there, otherwise to the portal.
 */
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { PageLoader } from '../../Components/LoadingSpinner';
import { guestApi } from '../../api/guestApi';
import GuestTaskPage from './GuestTaskPage';
import { GuestLinkRefusalPage, GuestLoadErrorPage, GuestLoadingPage, GuestUnavailablePage } from './components/GuestLinkStatePages';
import { useGuestFetch } from './hooks/useGuestFetch';
import './GuestTaskPage.css';

const GuestWorkOrderRoute: React.FC = () => {
    const { token, workOrderKey } = useParams<{ token: string; workOrderKey: string }>();
    const fromProjectKey = (useLocation().state as { projectKey?: string } | null)?.projectKey;
    const { data, loading, refusal, error, notFound } = useGuestFetch(
        () => guestApi.getWorkOrder(token as string, workOrderKey as string),
        token && workOrderKey ? `${token}/${workOrderKey}` : null
    );

    if (loading) {
        return (
            <GuestLoadingPage>
                <PageLoader text="Laddar..." />
            </GuestLoadingPage>
        );
    }
    if (refusal && token) {
        return <GuestLinkRefusalPage token={token} refusal={refusal} />;
    }
    if (error && !notFound && token) {
        return <GuestLoadErrorPage title="Kunde inte ladda ärendet" message={error} />;
    }
    if (!data || !token || !workOrderKey) {
        return (
            <GuestUnavailablePage
                title="Ärendet är inte längre tillgängligt"
                text="Det kan ha arkiverats eller delegerats till någon annan."
                token={token}
            />
        );
    }

    return (
        <GuestTaskPage
            token={token}
            initialData={data}
            workOrderKey={workOrderKey}
            back={fromProjectKey
                ? { href: `/guest/${token}/project/${fromProjectKey}`, label: 'Tillbaka till projektet' }
                : { href: `/guest/${token}`, label: 'Alla mina uppgifter' }}
        />
    );
};

export default GuestWorkOrderRoute;
